import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaTrash, FaPlus } from "react-icons/fa";
import debounce from "lodash.debounce";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { captionLengthTone } from "../../../helpers/captionBudget";

const formatDateForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  const month = `0${d.getMonth() + 1}`.slice(-2);
  const day = `0${d.getDate()}`.slice(-2);
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const EventDataSection = ({
  eventCaption2,
  setEventCaption2,
  eventHashtags,
  setEventHashtags,
  eventImageUrl,
  setEventImageUrl,
  eventAdditionalImages,
  setEventAdditionalImages,
  eventAdditionalDesc,
  setEventAdditionalDesc,
  advideoUrl,
  setAdVideoUrl,
  demoVideoUrl,
  setDemoVideoUrl,
  handleFileUpload,
  isEditMode,
  onUploadingChange,
  errors = {},
  setErrors = () => { },
}) => {
  const [localEventImagePreview, setLocalEventImagePreview] = useState(null);
  const [localAdditionalImagePreviews, setLocalAdditionalImagePreviews] =
    useState([]);
  const [localEventCaption2, setLocalEventCaption2] = useState(
    eventCaption2 || ""
  );
  const [newHashtag, setNewHashtag] = useState("");

  // Use refs to track if user is typing to prevent unwanted resets
  const isTypingCaption2 = useRef(false);

  // Sync props to local state when props change (but not while user is typing)
  useEffect(() => {
    if (!isTypingCaption2.current && eventCaption2 !== localEventCaption2) {
      setLocalEventCaption2(eventCaption2 || "");
    }
  }, [eventCaption2]);

  // Track in-flight uploads
  const [uploadCount, setUploadCount] = useState(0);
  const inc = () => setUploadCount((c) => c + 1);
  const dec = () => setUploadCount((c) => Math.max(0, c - 1));
  const blobUrlsRef = useRef([]);

  // notify parent when uploading state changes
  useEffect(() => {
    if (typeof onUploadingChange === "function") {
      onUploadingChange(uploadCount > 0);
    }
  }, [uploadCount, onUploadingChange]);

  // Helper to store and cleanup blob urls
  const makePreview = (file) => {
    const url = URL.createObjectURL(file);
    blobUrlsRef.current.push(url);
    return url;
  };

  useEffect(() => {
    return () => {
      // cleanup any created blob URLs
      blobUrlsRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch { }
      });
      blobUrlsRef.current = [];
    };
  }, []);

  // Handle Event Image Upload
  const handleEventImageUpload = useCallback(
    async (file) => {
      if (!file) return;
      const previewUrl = makePreview(file);
      setLocalEventImagePreview(previewUrl);

      try {
        inc();
        const uploadedUrl = await handleFileUpload(file);
        setEventImageUrl(uploadedUrl || null);
      } catch (e) {
        console.error("Event image upload failed:", e);
        setEventImageUrl(null);
      } finally {
        dec();
      }
    },
    [handleFileUpload, setEventImageUrl]
  );



  // Handle Additional Image Upload
  const handleAdditionalImageUpload = useCallback(
    async (file, index) => {
      if (!file) return;
      const previewUrl = makePreview(file);
      setLocalAdditionalImagePreviews((prev) => {
        const updated = [...prev];
        updated[index] = previewUrl;
        return updated;
      });

      try {
        inc();
        const uploadedUrl = await handleFileUpload(file);
        setEventAdditionalImages((prevImages) => {
          const arr = Array.isArray(prevImages) ? [...prevImages] : [];
          arr[index] = { url: uploadedUrl || null };
          return arr;
        });
      } catch (e) {
        console.error("Additional image upload failed:", e);
        setEventAdditionalImages((prevImages) => {
          const arr = Array.isArray(prevImages) ? [...prevImages] : [];
          arr[index] = { url: null };
          return arr;
        });
      } finally {
        dec();
      }
    },
    [handleFileUpload, setEventAdditionalImages]
  );

  // Add/Remove Additional Image
  const handleAddImage = useCallback(() => {
    setEventAdditionalImages((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      { url: null },
    ]);
    setLocalAdditionalImagePreviews((prev) => [...prev, null]);
  }, [setEventAdditionalImages]);

  const handleRemoveImage = useCallback(
    (index) => {
      setEventAdditionalImages((prev) =>
        Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
      );
      setLocalAdditionalImagePreviews((prev) =>
        prev.filter((_, i) => i !== index)
      );
    },
    [setEventAdditionalImages]
  );

  const handleTextareaResize = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  // Sync Previews with Edit Mode Data (when editing existing event)
  useEffect(() => {
    if (isEditMode) {
      setLocalEventImagePreview(eventImageUrl || null);
      setLocalAdditionalImagePreviews(
        Array.isArray(eventAdditionalImages)
          ? eventAdditionalImages.map((img) => (img && img.url) || null)
          : []
      );
      // Only update captions if not currently typing
      if (!isTypingCaption2.current) {
        setLocalEventCaption2(eventCaption2 || "");
      }
    }
  }, [
    isEditMode,
    eventImageUrl,
    eventAdditionalImages,
  ]);

  // Keep local single-image preview in sync with persisted URL
  useEffect(() => {
    // If we don’t have a local blob preview (or it’s gone after remount),
    // rehydrate from the saved URL so the image shows when returning to the step.
    if (
      eventImageUrl &&
      (!localEventImagePreview || !localEventImagePreview.startsWith("blob:"))
    ) {
      setLocalEventImagePreview(eventImageUrl);
    }
  }, [eventImageUrl, localEventImagePreview]);

  // Debounced update to parent state for captions
  const debouncedUpdateEventCaption2 = useCallback(
    debounce((value) => {
      setEventCaption2(value);
      isTypingCaption2.current = false;
    }, 500),
    [setEventCaption2]
  );

  const handleCaption2Change = (e) => {
    const value = e.target.value;
    isTypingCaption2.current = true;
    setLocalEventCaption2(value);
    debouncedUpdateEventCaption2(value);
    if (errors.eventCaption2) setErrors({ ...errors, eventCaption2: false });
  };

  // --- Hashtags helpers ---

  // Normalize: trim and remove any leading "#"
  const normalizeTag = (s = "") => s.trim().replace(/^#+/, "");

  // Add one or more hashtags (supports comma-separated input)
  const addHashtag = useCallback(() => {
    const parts = (newHashtag ?? "").split(",").map(normalizeTag).filter(Boolean);
    if (parts.length === 0) return;
    const existingLower = (eventHashtags || []).map((t) => String(t).toLowerCase());
    const newTags = parts.filter((t) => !existingLower.includes(t.toLowerCase()));
    if (newTags.length > 0) {
      setEventHashtags([...(eventHashtags || []), ...newTags]);
      if (errors.eventHashtags) setErrors({ ...errors, eventHashtags: false });
    }
    setNewHashtag("");
  }, [newHashtag, eventHashtags, setEventHashtags, errors, setErrors]);

  const handleHashtagPaste = useCallback((e) => {
    const pasted = e.clipboardData.getData("text");
    const parts = pasted.split(",").map(normalizeTag).filter(Boolean);
    if (parts.length <= 1) return; // let default paste happen for single tags
    e.preventDefault();
    const existingLower = (eventHashtags || []).map((t) => String(t).toLowerCase());
    const newTags = parts.filter((t) => !existingLower.includes(t.toLowerCase()));
    if (newTags.length > 0) {
      setEventHashtags([...(eventHashtags || []), ...newTags]);
      if (errors.eventHashtags) setErrors({ ...errors, eventHashtags: false });
    }
    setNewHashtag("");
  }, [eventHashtags, setEventHashtags, errors, setErrors]);

  // Remove hashtag by index (unchanged)
  const removeHashtag = useCallback(
    (index) => {
      setEventHashtags((prev) =>
        Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
      );
    },
    [setEventHashtags]
  );


  return (
    <div className="p-4 border rounded-md bg-blue-50 shadow-md">
      {/* Uploading banner */}
      {uploadCount > 0 && (
        <div className="mb-4 p-3 rounded bg-yellow-100 text-yellow-800 text-sm">
          Uploading files… Please wait. You can’t proceed until uploads finish.
        </div>
      )}

      {/* Duplicate Editable Checkboxes removed — using Participation tab version only */}




      {/* Event Captions */}
      <div className="mb-4">
        <label className={`text-lg font-semibold mb-2 flex items-center justify-between ${errors.eventCaption2 ? 'text-red-500' : ''}`}>
          <span>
            Event Referral Caption {errors.eventCaption2 && <span className="text-red-500 text-sm ml-2 font-normal">*Required</span>}
          </span>
          {(() => {
            const len = (localEventCaption2 || "").length;
            // No hard cap here: this field is no longer part of the Instagram
            // post (which had the 2200 limit) — it's a short WhatsApp/Telegram
            // footer, where that cap doesn't apply. Just count + soft-nudge to
            // keep it short.
            const tone = captionLengthTone(len, 800);
            const color = tone === "red" ? "text-red-600 bg-red-50" : tone === "yellow" ? "text-yellow-700 bg-yellow-50" : "text-gray-500 bg-gray-100";
            return (
              <span className={`text-xs font-normal px-2 py-0.5 rounded ${color}`}>
                {len} chars · added only when sharing to WhatsApp/Telegram (not the Instagram post) — keep it a short footer (e.g. "Cast your vote!")
              </span>
            );
          })()}
        </label>
        <textarea
          value={localEventCaption2}
          onChange={handleCaption2Change}
          onInput={handleTextareaResize}
          className={`w-full lg:min-h-40 border rounded-md px-4 py-2 ${errors.eventCaption2 ? 'border-red-500 error-field' : ''}`}
        />
      </div>

      {/* Event Hashtags hidden — using Participation tab version */}

      {/* Standard Media Section — always visible */}
      {/* Event Image */}
      <div className="mb-4">
        <label className="block text-lg font-semibold mb-2">Event Image</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handleEventImageUpload(e.target.files?.[0])}
          className="border rounded-md p-2"
        />
        {(localEventImagePreview || eventImageUrl) && (
          <img
            src={localEventImagePreview || eventImageUrl}
            alt="Event"
            className="mt-4 w-60 h-60 object-cover rounded-md"
          />
        )}
      </div>

      {/* Additional Images — Table Layout */}
      <div className="mb-4">
        <label className="block text-lg font-medium mb-2">
          Add Images that you want to show in event share time!
        </label>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left px-3 py-2 border-b font-semibold text-gray-600 w-10">#</th>
                <th className="text-left px-3 py-2 border-b font-semibold text-gray-600 w-20">Preview</th>
                <th className="text-left px-3 py-2 border-b font-semibold text-gray-600">Select File</th>
                <th className="px-3 py-2 border-b font-semibold text-gray-600 w-14 text-center">Del</th>
              </tr>
            </thead>
            <tbody>
              {(eventAdditionalImages || []).length === 0 && (
                <tr className="bg-white">
                  <td className="px-3 py-3 text-gray-400 align-middle">1</td>
                  <td className="px-3 py-3 align-middle">
                    <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                      <FaPlus />
                    </div>
                  </td>
                  <td className="px-3 py-3 align-middle">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleAdditionalImageUpload(e.target.files?.[0], 0)}
                      className="text-sm"
                    />
                  </td>
                  <td className="px-3 py-3 align-middle text-center text-gray-300">—</td>
                </tr>
              )}
              {(eventAdditionalImages || []).map((image, index) => (
                <tr key={index} className="border-t bg-white hover:bg-gray-50">
                  <td className="px-3 py-3 text-gray-500 align-middle">{index + 1}</td>
                  <td className="px-3 py-3 align-middle">
                    {(localAdditionalImagePreviews[index] || image?.url) ? (
                      <img
                        src={localAdditionalImagePreviews[index] || image.url}
                        alt={`img-${index + 1}`}
                        className="w-14 h-14 object-cover rounded"
                      />
                    ) : (
                      <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                        <FaPlus />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 align-middle">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleAdditionalImageUpload(e.target.files?.[0], index)}
                      className="text-sm"
                    />
                  </td>
                  <td className="px-3 py-3 align-middle text-center">
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-3 bg-gray-50 border-t">
            <button
              type="button"
              onClick={handleAddImage}
              className="px-4 py-2 bg-green-500 text-white rounded-md flex items-center gap-2 text-sm hover:bg-green-600 transition"
            >
              <FaPlus /> Add Image
            </button>
          </div>
        </div>
      </div>

      {/* Additional Description */}
      <div className="mb-4">
        <label className="block text-lg font-medium mb-2">
          Add Description that you want to show in event share time!
        </label>
        <textarea
          value={eventAdditionalDesc || ""}
          onChange={(e) => setEventAdditionalDesc(e.target.value)}
          onInput={handleTextareaResize}
          className="w-full lg:min-h-40 border rounded-md px-4 py-2"
        />
      </div>

    </div>
  );
};

export default EventDataSection;
